// ============================================================
// PAYMENT PLANS ADMIN HOOK
// ============================================================

import { useEffect, useState } from 'react';
import { api } from '../services/api';
import type { PaymentPlan } from '../types/payment.types';

interface PaymentPlansStats {
  total: number;
  active: number;
  overdue: number;
  completed: number;
  totalAmount: number;
}

export function usePaymentPlansAdmin() {
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchPaymentPlans();
  }, [statusFilter]);

  const fetchPaymentPlans = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await api.get('/admin/payment-plans', { params });

      if (data.success) {
        setPaymentPlans(data.data);
      }
    } catch (err) {
      setError('Erreur lors du chargement des plans de paiement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage côté client
  const filteredPlans = paymentPlans.filter((plan) => {
    const matchesSearch =
      !searchQuery ||
      plan.booking?.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.booking?.customerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plan.booking?.reference?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);
  const paginatedPlans = filteredPlans.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Statistiques globales (non filtrées) - Les KPI doivent rester fixes
  const [globalStats, setGlobalStats] = useState<PaymentPlansStats>({
    total: 0,
    active: 0,
    overdue: 0,
    completed: 0,
    totalAmount: 0,
  });

  // Calculer les stats globales une seule fois au chargement
  useEffect(() => {
    if (paymentPlans.length > 0 && statusFilter === 'all') {
      setGlobalStats({
        total: paymentPlans.length,
        active: paymentPlans.filter((p) => p.status === 'IN_PROGRESS' || p.status === 'PENDING_PAYMENT').length,
        overdue: paymentPlans.filter((p) => p.status === 'OVERDUE').length,
        completed: paymentPlans.filter((p) => p.status === 'COMPLETED').length,
        totalAmount: paymentPlans.reduce((sum, p) => sum + p.totalAmount, 0),
      });
    }
  }, [paymentPlans, statusFilter]);

  const stats = globalStats;

  const hasActiveFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  const resetFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const updateSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const updateStatusFilter = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  return {
    paymentPlans: paginatedPlans,
    loading,
    error,
    stats,
    statusFilter,
    searchQuery,
    hasActiveFilters,
    pagination: {
      currentPage,
      totalPages,
      total: filteredPlans.length,
    },
    updateSearch,
    updateStatusFilter,
    resetFilters,
    setPage: setCurrentPage,
    refresh: fetchPaymentPlans,
  };
}
